import { useEffect, useState, useCallback } from 'react';
import { TOAST_EVENT, type ToastDetail } from '../../lib/toast';

/**
 * F-003 — Toast primitive.
 *
 * Notification queue that listens for `moderation:toast` events and
 * renders dismissible toasts in the bottom-right. Auto-dismisses after
 * `durationMs`. Success toasts use `role=status`, error toasts use
 * `role=alert` so assistive tech treats them appropriately.
 *
 * Hand-rolled with no Radix dependency: matches the existing primitive
 * style (Dialog, Popover, Select) and keeps the bundle lean.
 */

interface ToastItem extends ToastDetail {
  /** Whether the toast is in its exit animation. */
  exiting?: boolean;
}

export default function ToastContainer() {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ToastDetail>).detail;
      if (!detail?.message) return;
      setItems((prev) => [...prev, { ...detail }]);
      if (detail.durationMs > 0) {
        window.setTimeout(() => {
          dismiss(detail.id);
        }, detail.durationMs);
      }
    };
    window.addEventListener(TOAST_EVENT, handler);
    return () => window.removeEventListener(TOAST_EVENT, handler);
  }, [dismiss]);

  if (items.length === 0) return null;

  return (
    <div
      className="toast-container"
      aria-live="polite"
      aria-relevant="additions"
      data-testid="toast-container"
    >
      {items.map((t) => (
        <div
          key={t.id}
          className={`toast toast--${t.tone}`}
          role={t.tone === 'error' ? 'alert' : 'status'}
          data-testid={`toast-${t.tone}`}
        >
          <span className="toast__message">{t.message}</span>
          <button
            type="button"
            className="toast__dismiss"
            aria-label="Dismiss notification"
            onClick={() => dismiss(t.id)}
            data-testid="toast-dismiss"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
